import * as React from "react";
import {useTheme} from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {
  Card,
  Divider,
  Grid,
  TextField,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  Tooltip,
  Typography
} from "@mui/material";
import MDButton from "components/MDButton";
import dayjs from "dayjs";
import {DataGrid, GridActionsCellItem} from "@mui/x-data-grid";
import {GET, POST} from "APIclient";
import {useEffect, useState} from "react";
import {toast} from "react-toastify";
import DoneIcon from "@mui/icons-material/Done";
import ClearIcon from "@mui/icons-material/Clear";
import {Dialog, DialogTitle, DialogContent, DialogActions} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {LastPage, FirstPage, NavigateBefore, NavigateNext} from "@mui/icons-material";
import {Snackbar, Alert} from "@mui/material";
import properties from "constants/ApplicationConstants";
import {formatDate} from "constants/ApplicationConstants";
import MDInput from "components/MDInput";

function CustomPagination({
  page,
  pageSize,
  totalItems,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading
}) {
  const handleFirstPage = () => {
    onPageChange(0);
  };

  const handlePreviousPage = () => {
    onPageChange(Math.max(0, page - 1));
  };

  const handleNextPage = () => {
    onPageChange(Math.min(totalPages - 1, page + 1));
  };

  const handleLastPage = () => {
    onGoToLastPage();
  };

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalItems);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderTop: "1px solid #e0e0e0"
      }}
    >
      <Typography variant="caption" sx={{color: "#666"}}>
        {totalItems > 0 ? `${startRow}-${endRow} of ${totalItems}` : "No rows"}
      </Typography>

      <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
        <Typography variant="caption" sx={{mr: 2, color: "#666"}}>
          Page {page + 1} of {totalPages || 1}
        </Typography>

        <Tooltip title="First Page">
          <IconButton onClick={handleFirstPage} disabled={page === 0 || loading} size="small">
            <FirstPage />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous Page">
          <IconButton onClick={handlePreviousPage} disabled={page === 0 || loading} size="small">
            <NavigateBefore />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor: properties?.colors?.bgcolorbluethem || "#1565c0"
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e"
              }
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

const columns = [
  {field: "id", headerName: "#", width: 20, headerAlign: "center", align: "center"},
  {
    field: "empName",
    headerName: "Employee Name",
    width: 140,
    headerAlign: "center",
    align: "center"
  },
  {field: "empId", headerName: "EMP ID", width: 70, headerAlign: "center", align: "center"},
  {
    field: "AppliedOn",
    headerName: "Worked On",
    width: 105,
    headerAlign: "center",
    align: "center"
  },
  {
    field: "approvedDate",
    headerName: "Approved Date",
    width: 120,
    headerAlign: "center",
    align: "center"
  },
  {
    field: "expiredDate",
    headerName: "Expired Date",
    width: 120,
    headerAlign: "center",
    align: "center"
  },
  {
    field: "reason",
    headerName: "Reason",
    width: 180,
    headerAlign: "center",
    align: "center"
  },
  {
    field: "status",
    headerName: "Status",
    width: 120,
    headerAlign: "center",
    align: "center",
    renderCell: params => {
      const status = params.value.toLowerCase();
      let buttonColor;
      let statusText;

      switch (status) {
        case "pending":
          buttonColor = "warning";
          statusText = "Pending";
          break;
        case "approved":
          buttonColor = "success";
          statusText = "Approved";
          break;
        case "rejected":
          buttonColor = "error";
          statusText = "Rejected";
          break;
        case "cancelled":
          buttonColor = "error";
          statusText = "Cancelled";
          break;
        case "used":
          buttonColor = "success";
          statusText = "Used";
          break;
        case "not used":
          buttonColor = "warning";
          statusText = "Not Used";
          break;
        case "expired":
          buttonColor = "error";
          statusText = "Expired";
          break;
        default:
          buttonColor = "default";
          statusText = status;
      }

      return (
        <MDButton
          variant="contained"
          color={buttonColor}
          size="small"
          disabled
          sx={{
            textTransform: "capitalize",
            fontSize: "0.800rem",
            width: "70px",
            justifyContent: "center"
          }}
        >
          {statusText}
        </MDButton>
      );
    }
  },
  {
    field: "actions",
    headerName: "Action",
    width: 110,
    headerAlign: "center",
    align: "center",
    renderCell: params => {
      const status = params.row.status.toLowerCase();
      const isActionDisabled =
        status === "approved" ||
        status === "rejected" ||
        status === "cancelled" ||
        status === "used" ||
        status === "expired";

      return [
        <Tooltip title="Approve Leave" arrow key={`approve-${params.id}`}>
          <GridActionsCellItem
            icon={<DoneIcon />}
            label="Approve"
            color="success"
            onClick={() => params.row.handleApprove(params.row)}
            disabled={isActionDisabled}
          />
        </Tooltip>,
        <Tooltip title="Reject Leave" arrow key={`reject-${params.id}`}>
          <GridActionsCellItem
            icon={<ClearIcon />}
            label="Reject"
            color="error"
            onClick={() => params.row.handleReject(params.row)}
            disabled={isActionDisabled}
          />
        </Tooltip>,
        <Tooltip title="View Details" arrow key={`view-${params.id}`}>
          <GridActionsCellItem
            icon={<VisibilityIcon />}
            label="View"
            color="primary"
            onClick={() => params.row.handleView(params.row)}
          />
        </Tooltip>
      ];
    }
  }
];

export default function AdminListCompensatoryOff() {
  const empId = Number(window.sessionStorage.getItem("empId"));
  const theme = useTheme();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [filteredLeaveRequests, setFilteredLeaveRequests] = useState([]);
  const [paginationModel, setPaginationModel] = useState({page: 0, pageSize: 20});
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [selectedLeaveRequest, setSelectedLeaveRequest] = useState(null);
  const [actionType, setActionType] = useState("");
  const [reason, setReason] = useState("");
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [updateFlag, setUpdateFlag] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState("success");

  const [searchParams, setSearchParams] = useState({
    empId: "",
    empName: "",
    isUsed: ""
  });

  const handleClose = () => {
    setSnackOpen(false);
  };

  const resetActionState = () => {
    setReason("");
    setSelectedLeaveRequest(null);
    setActionType("");
  };

  const handleViewDetails = leaveRequest => {
    setSelectedLeaveRequest(leaveRequest);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedLeaveRequest(null);
  };

  const handleApprove = row => {
    resetActionState();

    setSelectedLeaveRequest(row);
    setActionType("approve");
    setActionModalOpen(true);
  };

  const handleReject = row => {
    resetActionState();

    setSelectedLeaveRequest(row);
    setActionType("reject");
    setActionModalOpen(true);
  };

  const handleSubmitAction = async () => {
    if (!reason) {
      setSnackMessage("Please provide a reason for the action.");
      setSnackColor("error");
      setSnackOpen(true);
      return;
    }

    try {
      const url = "manager/coffRequest";
      const payload = {
        loggedInEmpId: empId,
        action: actionType,
        reason: reason,
        checked_list: [selectedLeaveRequest.leaveRequestId]
      };

      const response = await POST(url, payload);
      if (response.status === 200) {
        setSnackMessage(response.data.message);
        setSnackColor("success");
        setSnackOpen(true);
        setLeaveRequests(prev =>
          prev.map(req => {
            if (req.leaveRequestId === selectedLeaveRequest.leaveRequestId) {
              return {...req, status: actionType === "approve" ? "Approved" : "Rejected"};
            }
            return req;
          })
        );
        setFilteredLeaveRequests(prev =>
          prev.map(req => {
            if (req.leaveRequestId === selectedLeaveRequest.leaveRequestId) {
              return {...req, status: actionType === "approve" ? "Approved" : "Rejected"};
            }
            return req;
          })
        );
        setActionModalOpen(false);
        setReason("");
        setUpdateFlag(prev => !prev);
      }
    } catch (error) {
      setSnackMessage(
        error.response?.data?.message || "An error occurred while submitting the COFF request."
      );
      setSnackColor("error");
      setSnackOpen(true);
    }
  };

  const handleSearchChange = event => {
    const {name, value} = event.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = () => {
    const newPaginationModel = {page: 0, pageSize: paginationModel.pageSize};
    setPaginationModel(newPaginationModel);
    fetchLeaveRequests(0, paginationModel.pageSize, searchParams);
  };

  const fetchLeaveRequests = async (page, pageSize, searchParams = {}) => {
    if (isFetching) return;
    setIsFetching(true);
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        empId: searchParams.empId || "",
        empName: searchParams.empName || "",
        isUsed: searchParams.isUsed || ""
      });

      const url = `admin/leave/search-coffRequest?${queryParams.toString()}`;
      const response = await GET(url);

      if (response.status === 200) {
        const data = response.data.data.paginatedData || [];
        const totalRecords = response.data.data.totalItems || 0;
        const totalPages = response.data.data.totalPages || 0;

        let index = page * pageSize + 1;
        const formattedData = data.map(item => {
          let statusLabel = "";
          if (item.isUsed === 0) {
            statusLabel = "Not Used";
          } else if (item.isUsed === 1) {
            statusLabel = "Used";
          } else if (item.isUsed === 2) {
            statusLabel = "Expired";
          } else if (item.status === 1) {
            statusLabel = "Pending";
          } else if (item.approved === 1) {
            statusLabel = "Approved";
          } else if (item.status === 2) {
            statusLabel = "Rejected";
          } else if (item.cancel === 1) {
            statusLabel = "Cancelled";
          }

          return {
            id: index++,
            empId: item.empId,
            empName: `${item.firstName} ${item.lastName}`,
            mobileNo: item.mobileNo || "N/A",
            AppliedOn: formatDate(item.applyDate),
            approvedDate: formatDate(item.approvDate),
            expiredDate: formatDate(item.expireDate),
            status: statusLabel,
            reason: item.reason || "No Reason Provided",
            leaveRequestId: item.coffLeaveId,
            handleApprove,
            handleReject,
            handleView: handleViewDetails
          };
        });

        setLeaveRequests(formattedData);
        setFilteredLeaveRequests(formattedData);
        setTotalItems(totalRecords);
        setTotalPages(totalPages);
      } else {
        setSnackMessage("Failed to fetch COFF requests.");
        setSnackColor("error");
        setSnackOpen(true);
      }
    } catch (error) {
      setSnackMessage(error.response?.data?.message || "Error fetching COFF requests.");
      setSnackColor("error");
      setSnackOpen(true);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchLeaveRequests(paginationModel.page, paginationModel.pageSize, searchParams);
  }, [empId, paginationModel.page, paginationModel.pageSize, updateFlag]);

  const handlePaginationChange = newPaginationModel => {
    setPaginationModel(newPaginationModel);
    fetchLeaveRequests(newPaginationModel.page, newPaginationModel.pageSize, searchParams);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handlePageChange = newPage => {
    const newPaginationModel = {...paginationModel, page: newPage};
    setPaginationModel(newPaginationModel);
    fetchLeaveRequests(newPage, paginationModel.pageSize, searchParams);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleGoToLastPage = () => {
    if (totalPages > 0) {
      const lastPageIndex = totalPages - 1;
      const newPaginationModel = {...paginationModel, page: lastPageIndex};
      setPaginationModel(newPaginationModel);
      fetchLeaveRequests(lastPageIndex, paginationModel.pageSize, searchParams);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 0);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox container pt={2} pb={2} px={3}>
              <MDBox sx={{display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 1.5}}>
                <Typography
                  sx={{
                    gridColumn: "span 3",
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "15px",
                    fontWeight: "bold"
                  }}
                >
                  Compensatory Off Requests
                </Typography>
                <MDInput
                  sx={{gridColumn: "span 2"}}
                  value={searchParams.empId}
                  onChange={handleSearchChange}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  name="empId"
                  label="Employee ID"
                  variant="outlined"
                />
                <MDInput
                  sx={{gridColumn: "span 2"}}
                  value={searchParams.empName}
                  onChange={handleSearchChange}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  name="empName"
                  label="Employee Name"
                  variant="outlined"
                />
                <FormControl sx={{gridColumn: "span 2"}}>
                  <InputLabel id="status-select-label">Status</InputLabel>
                  <Select
                    labelId="status-select-label"
                    value={searchParams.isUsed}
                    label="Status"
                    name="isUsed"
                    onChange={handleSearchChange}
                    variant="outlined"
                    sx={{
                      height: "45px",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="1">Used</MenuItem>
                    <MenuItem value="2">Expired</MenuItem>
                    <MenuItem value="0">Not Used</MenuItem>
                  </Select>
                </FormControl>
                <MDButton
                  sx={{
                    gridColumn: "span 2",
                    fontFamily: properties.fontFamily,
                    background: properties.colors.bgcolorbluethem
                  }}
                  variant="contained"
                  color="info"
                  onClick={handleSearch}
                >
                  Search
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>

        <MDBox>
          <Paper>
            <DataGrid
              rows={filteredLeaveRequests}
              columns={columns}
              paginationMode="server"
              rowCount={totalItems}
              pageSizeOptions={[20]}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              hideFooter
              size="small"
              sx={{
                fontFamily: properties.fontFamily,
                fontSize: "14px",
                fontWeight: 400,
                height: "450px",
                "& .MuiDataGrid-scrollbar": {
                  width: "10px !important"
                }
              }}
              aria-label="a dense table"
            />
            <CustomPagination
              page={paginationModel.page}
              pageSize={paginationModel.pageSize}
              totalItems={totalItems}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onGoToLastPage={handleGoToLastPage}
              loading={isFetching}
            />
          </Paper>
        </MDBox>

        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              width: "700px",
              maxWidth: "none"
            }
          }}
        >
          <DialogTitle
            sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}
          >
            <MDTypography variant="h6" sx={{fontFamily: properties.fontFamily}}>
              Compensatory Leave Request Details
            </MDTypography>
          </DialogTitle>
          <DialogContent sx={{padding: 2}}>
            {selectedLeaveRequest && (
              <form>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="Employee ID"
                      value={selectedLeaveRequest.empId || "No ID Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Employee Name"
                      value={selectedLeaveRequest.empName || "No Name Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Coff Against"
                      value={selectedLeaveRequest.AppliedOn || "No Coff Type Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Status"
                      value={selectedLeaveRequest.status || "No status available"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Reason"
                      value={selectedLeaveRequest.reason || "No reason provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                </Grid>
                <Divider sx={{my: 2}} />
              </form>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseModal} variant="contained">
              Close
            </MDButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={actionModalOpen}
          onClose={() => {
            setActionModalOpen(false);
            setReason("");
            setSelectedLeaveRequest(null);
            setActionType("");
          }}
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              width: "700px",
              maxWidth: "none"
            }
          }}
        >
          <DialogTitle
            sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}
          >
            <MDTypography variant="h6" sx={{fontFamily: properties.fontFamily}}>{`${
              actionType === "approve" ? "Approve" : "Reject"
            } Leave Request`}</MDTypography>
          </DialogTitle>
          <DialogContent>
            {selectedLeaveRequest && (
              <form>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="Employee ID"
                      value={selectedLeaveRequest?.empId || "No ID Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Employee Name"
                      value={selectedLeaveRequest?.empName || "No Name Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Coff Against"
                      value={selectedLeaveRequest?.AppliedOn || "No Coff Type Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Status"
                      value={selectedLeaveRequest.status || "No status available"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Reason"
                      value={selectedLeaveRequest?.reason || "No Reason Provided"}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      InputProps={{readOnly: true}}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={
                        <span>
                          Manager&#39;s Reason<span style={{color: properties.colors.Red}}>*</span>
                        </span>
                      }
                      value={reason}
                      onChange={e => setReason(e.target.value)}
                      fullWidth
                      variant="outlined"
                      margin="normal"
                    />
                  </Grid>
                </Grid>
              </form>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton
              color="secondary"
              onClick={() => {
                setActionModalOpen(false);
                resetActionState();
              }}
              variant="contained"
            >
              Cancel
            </MDButton>
            <MDButton color="primary" onClick={handleSubmitAction} variant="contained">
              Submit
            </MDButton>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackOpen}
          anchorOrigin={{vertical: "top", horizontal: "right"}}
          autoHideDuration={6000}
          onClose={handleClose}
        >
          <Alert
            onClose={handleClose}
            severity={snackColor === "success" ? "success" : "error"}
            variant="filled"
            sx={{width: "100%", color: "#fff"}}
          >
            {snackMessage}
          </Alert>
        </Snackbar>
      </MDBox>
    </DashboardLayout>
  );
}
